import { RawPointerEvent, UIEventListeners } from '../../../../common/src/ts/types/uievent';
import { Action, Dispatch } from 'redux';
import * as ViewAction from '../actions/ViewAction';
import FrameStore, { getGlobalFrameStore } from '../services/FrameStore';
import { isTouchEvent, isPointerEvent } from '../../../../common/src/ts/utils/uievent';
import { ViewerToContent } from '../types/messages';
import { markerAppend } from '../marker/actions/MarkerAction';
import { cfiFragment, localPath, CFIRange } from '../../../../common/src/ts/cfi';
import { parseAll } from '../../../../common/src/ts/parser';
import { setSelectionState } from '../actions/SelectionAction';
import { SelectionType } from '../reducers/selection';
import { MarkerToolState } from '../marker/reducers/marker';
import { find } from '../../../../common/src/ts/utils';
import { isPopupPanelID } from '../reducers/pages';
import * as PageAction from '../actions/PageAction';

export function createDefaultUIEventListeners(
  dispatch: Dispatch<Action>,
  panelID: string,
): UIEventListeners {
  return {
    onPointerDown(ev: RawPointerEvent) {
      ev.stopPropagation();
      if (isPopupPanelID(panelID)) {
        dispatch(PageAction.setFocus(panelID));
      }
      dispatch(ViewAction.makePointerAction(ViewAction.VIEW_POINTER_DOWN, panelID, ev));
    },
    onPointerUp(ev: RawPointerEvent) {
      ev.stopPropagation();
      dispatch(ViewAction.makePointerAction(ViewAction.VIEW_POINTER_UP, panelID, ev));
    },
    onPointerMove(ev: RawPointerEvent) {
      ev.stopPropagation();
      dispatch(ViewAction.makePointerAction(ViewAction.VIEW_POINTER_MOVE, panelID, ev));
    },

    onGestureStart(ev: GestureEvent) {
      ev.preventDefault();
      dispatch(ViewAction.gestureStart(panelID, ev));
    },
    onGestureChange(ev: GestureEvent) {
      ev.preventDefault();
      dispatch(ViewAction.gestureChange(panelID, ev));
    },
    onGestureEnd(ev: GestureEvent) {
      ev.preventDefault();
      dispatch(ViewAction.gestureEnd(panelID, ev));
    },
  };
}

function calculateInnerPosition(x: number, y: number, rect: ClientRect, scale: number) {
  return [(x - rect.left) / scale, (y - rect.top) / scale];
}

export function joinReference(opf: string, parent: string, child: string) {
  const parentFragment = parseAll(parent, localPath);
  if (parentFragment === undefined) {
    return;
  }
  const fragment = parseAll(child, cfiFragment);
  if (fragment === undefined) {
    return;
  }
  return `${opf}#${fragment.prependParent(parentFragment).toFragment()}`;
}

function isCFIRange(cfi: string) {
  return parseAll(cfi, cfiFragment) instanceof CFIRange;
}

class MarkerUIEventListeners implements UIEventListeners {
  private iframe?: HTMLIFrameElement;
  private touchID?: number;

  public constructor(
    private readonly tool: MarkerToolState,
    private readonly dispatch: Dispatch<Action>,
  ) {}

  private getTouchPosition(ev: TouchEvent) : [number, number] | undefined {
    const { changedTouches } = ev;
    switch (ev.type) {
      case 'touchstart': {
        if (this.touchID !== undefined || changedTouches.length < 1) {
          return;
        }
        const touch = changedTouches[0];
        this.touchID = touch.identifier;
        return [touch.clientX, touch.clientY];
      }
      case 'touchmove': {
        if (this.touchID === undefined) {
          return;
        }
        const touch = find(changedTouches, touch => touch.identifier === this.touchID);
        if (touch === undefined) {
          return;
        }
        return [touch.clientX, touch.clientY];
      }
      case 'touchend': {
        if (this.touchID === undefined) {
          return;
        }
        const touch = find(changedTouches, touch => touch.identifier === this.touchID);
        if (touch === undefined) {
          return;
        }
        this.touchID = undefined;
        return [touch.clientX, touch.clientY];
      }
    }
  }

  private getPointerPosition(ev: RawPointerEvent) : [number, number] | undefined {
    if (isTouchEvent(ev)) {
      return this.getTouchPosition(ev);
    }
    return [ev.clientX, ev.clientY];
  }

  private getVisualScale(iframe: HTMLIFrameElement, frameStore: FrameStore): number | undefined {
    const frameInfo = frameStore.getInfo(iframe);
    if (frameInfo === undefined) {
      return;
    }

    const ephemeralStyle = frameStore.getEphemeralStyle(frameInfo.frameID);
    if (ephemeralStyle === undefined) {
      return;
    }

    return ephemeralStyle.visualScale;
  }

  public onPointerDown = (ev: RawPointerEvent) => {
    const position = this.getPointerPosition(ev);
    if (position !== undefined) {
      const [clientX, clientY] = position;
      const store = getGlobalFrameStore();
      this.iframe = store.frameFromPoint(clientX, clientY);
      if (this.iframe !== undefined) {
        const visualScale = this.getVisualScale(this.iframe, store);
        if (visualScale === undefined) {
          return;
        }
        const rect = this.iframe.getBoundingClientRect();
        const [x, y] = calculateInnerPosition(clientX, clientY, rect, visualScale);
        store.sendTemporaryMarkerMessage(
          this.iframe.contentWindow,
          ViewerToContent.MessageType.StartTemporaryMarker,
          x,
          y,
        );
        if (isPointerEvent(ev)) {
          (ev.target as Element).setPointerCapture(ev.pointerId);
        }
      }
    }
  }

  public onPointerUp = async (ev: RawPointerEvent) => {
    const { iframe } = this;
    if (iframe !== undefined) {
      const position = this.getPointerPosition(ev);
      if (position !== undefined) {
        this.iframe = undefined;
        if (isPointerEvent(ev)) {
          (ev.target as Element).releasePointerCapture(ev.pointerId);
        }
        const store = getGlobalFrameStore();
        const visualScale = this.getVisualScale(iframe, store);
        if (visualScale === undefined) {
          return;
        }
        const [clientX, clientY] = position;
        const rect = iframe.getBoundingClientRect();
        const [x, y] = calculateInnerPosition(clientX, clientY, rect, visualScale);
        const result = store.sendTemporaryMarkerMessage(
          iframe.contentWindow,
          ViewerToContent.MessageType.EndTemporaryMarker,
          x,
          y,
        );
        if (result === undefined) {
          return;
        }
        const { cfi, text } = await result;
        if (cfi === undefined) {
          return;
        }
        if (isCFIRange(cfi)) {
          const info = store.getInfo(iframe);
          if (info === undefined) {
            return;
          }
          const reference = joinReference(info.reference[0], info.reference[1], cfi);
          if (reference === undefined) {
            return;
          }
          // XXX: Mock
          const colors = [
            'rgba(255, 0, 0, 0.25)',
            'rgba(0, 0, 255, 0.25)',
            'rgba(255, 255, 0, 0.25)',
            'rgba(0, 128, 0, 0.25)',
            'rgba(0, 0, 0, 0.25)',
          ];
          const color = colors[this.tool.colorIndex];
          this.dispatch(
            markerAppend({
              reference,
              text,
              style: `background-color:${color}`,
            }),
          );
        } else {
          const marker = store.sendSelectMarkerMessage(iframe.contentWindow, x, y);
          if (marker === undefined) {
            return;
          }
          const { id } = await marker;
          if (id !== undefined) {
            this.dispatch(setSelectionState({ id, type: SelectionType.MARKER }));
          } else {
            this.dispatch(setSelectionState({ type: SelectionType.NONE }));
          }
        }
      }
    }
  }
  public onPointerMove = (ev: RawPointerEvent) => {
    if (this.iframe !== undefined) {
      const position = this.getPointerPosition(ev);
      if (position !== undefined) {
        const store = getGlobalFrameStore();
        const visualScale = this.getVisualScale(this.iframe, store);
        if (visualScale === undefined) {
          return;
        }
        const [clientX, clientY] = position;
        const rect = this.iframe.getBoundingClientRect();
        const [x, y] = calculateInnerPosition(clientX, clientY, rect, visualScale);
        store.sendTemporaryMarkerMessage(
          this.iframe.contentWindow,
          ViewerToContent.MessageType.MoveTemporaryMarker,
          x,
          y,
        );
      }
    }
  }

  public onGestureStart = (ev: GestureEvent) => {
    ev.preventDefault();
  }

  public onGestureChange = (ev: GestureEvent) => {
    ev.preventDefault();
  }

  public onGestureEnd = (ev: GestureEvent) => {
    ev.preventDefault();
  }
}

export function createMarkerUIEventListeners(
  tool: MarkerToolState,
  dispatch: Dispatch<Action>,
): UIEventListeners {
  return new MarkerUIEventListeners(tool, dispatch);
}
